/**
 * Dual Pair Guarantees — three adjoint pair closures.
 *
 * Six of the ten Neutralization Theorems come in three adjoint pairs. Deploying
 * both theorems in a pair is strictly stronger than either alone, because the
 * adjunction unit/counit closes the complementary coverage gap.
 *
 * Agda source:  ai-resources/CSK-DUAL-PAIR-GUARANTEES.md
 *               agda/Research/CompositionPrinciples/DualityPrinciples.agda
 * Realization fidelity: STRUCTURAL — adjunction closure property verified on
 *   concrete instances; the unit/counit naturality squares are documented but
 *   TypeScript cannot prove them at the type level.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { ProvenancedData } from "./dataOrigin.js";
import type { ClassifiedData } from "./dataReach.js";
import type { EstablishedState, Invariant } from "./invEstablish.js";
import type { GuardedTransition } from "./invPreserve.js";
import type { ResourceHandle } from "./resConsume.js";
import type { ManagedResource } from "./resRelease.js";
import { preservingTransition } from "./invPreserve.js";
import { consume } from "./resConsume.js";
import { release } from "./resRelease.js";

// ── Shared: DualPair code ──────────────────────────────────────

/** The three dual pair codes — each names a Level-1 adjoint pair. */
export type DualPairCode = "DATA" | "INV" | "RES";

// ── TRACE — DATA dual pair (D1 ⊣ D2) ─────────────────────────
//
// Adjunction: fibration (DATA.ORIGIN) ⊣ opfibration (DATA.REACH)
// Gap without D1: reach tracked, origin unknown  — taint without source
// Gap without D2: origin tracked, reach unknown  — source without taint
// Combined:       complete bidirectional data lineage

/**
 * A bidirectional data lineage closure produced by combining D1 and D2.
 *
 * Neither witness alone is sufficient:
 *   - D1 alone proves WHERE data came from but not where it can go.
 *   - D2 alone proves WHERE data can go but not where it originated.
 *   - D1 + D2 closes the loop: origin is verified AND reachability is bounded.
 *
 * Agda correspondence: unit η : id → U∘F (origin tracked forward through reach)
 *                      counit ε : F∘U → id (reach recovers from origin)
 */
export interface DataLineageClosure<T, O extends string, C extends string> {
  /** The value with its provenance witness (from DATA.ORIGIN, D1). */
  readonly provenanced: ProvenancedData<T, O>;
  /** The value with its reach classification (from DATA.REACH, D2). */
  readonly classified: ClassifiedData<T, C>;
  /** Witness that both halves of the adjunction are satisfied. */
  readonly bidirectionalClosed: true;
  /** The dual pair code. */
  readonly pair: "DATA";
}

/**
 * Close the DATA dual pair: combine a provenance witness (D1) and a
 * reach classification (D2) into a complete bidirectional lineage closure.
 *
 * Both `provenanced` and `classified` must carry the same underlying value `T`.
 * The closure witnesses that the adjunction unit and counit both hold on this value.
 */
export function closeDataLineage<T, O extends string, C extends string>(
  provenanced: ProvenancedData<T, O>,
  classified: ClassifiedData<T, C>,
): DataLineageClosure<T, O, C> {
  return {
    provenanced,
    classified,
    bidirectionalClosed: true,
    pair: "DATA",
  };
}

// ── ALWAYS — INV dual pair (I1 ⊣ I2) ─────────────────────────
//
// Adjunction: Free (INV.ESTABLISH) ⊣ Cofree (INV.PRESERVE)
// Gap without I1: transitions checked, initial state unvalidated
// Gap without I2: initial state validated, transitions unchecked
// Combined:       all states — initial and every reachable successor — are valid

/**
 * A complete invariant discipline closure produced by combining I1 and I2.
 *
 * Neither theorem alone is sufficient:
 *   - I1 alone validates the initial state but transitions may break invariants.
 *   - I2 alone checks transitions but starts from an unvalidated initial state.
 *   - I1 + I2 guarantees every reachable state satisfies every invariant.
 *
 * Agda correspondence: Free ⊣ Cofree (F-algebra ↔ F-coalgebra adjunction)
 */
export interface InvariantDiscipline<S> {
  /** The validated initial state (from INV.ESTABLISH, I1). */
  readonly established: EstablishedState<S>;
  /** The current state after applying verified transitions. */
  readonly currentState: EstablishedState<S>;
  /** The number of verified transitions applied since establishment. */
  readonly transitionsApplied: number;
  /** Witness that every applied transition preserved the invariants. */
  readonly allTransitionsVerified: true;
  /** The dual pair code. */
  readonly pair: "INV";
}

/**
 * Establish an invariant discipline: validate the initial state (I1) and
 * apply a sequence of transitions verifying each preserves the invariants (I2).
 *
 * Throws if any invariant is not satisfied at establishment or after any transition.
 */
export function closeInvariantDiscipline<S>(
  established: EstablishedState<S>,
  transitions: readonly GuardedTransition<S>[],
  invariants: readonly Invariant<S>[],
): InvariantDiscipline<S> {
  let current = established;
  for (const transition of transitions) {
    current = preservingTransition(current, transition, invariants);
  }
  return {
    established,
    currentState: current,
    transitionsApplied: transitions.length,
    allTransitionsVerified: true,
    pair: "INV",
  };
}

// ── ACCOUNT — RES dual pair (R1 ⊣ R2) ────────────────────────
//
// Adjunction: (−⊗A) (RES.CONSUME) ⊣ (A*⊗−) (RES.RELEASE)
// Gap without R1: release tracked, double-spend possible
// Gap without R2: consumption tracked, leaks possible
// Combined:       every resource ends in exactly one terminal state: consumed or released

/** The terminal lifecycle mode for a resource. */
export type ResourceLifecycleMode = "consumed" | "released";

/**
 * A complete resource lifecycle record produced by finalizing a resource
 * through either consumption (R1) or release (R2).
 *
 * Neither theorem alone covers the full lifecycle:
 *   - R1 alone: without R2, double-spend is structurally inexpressible but leaks are not.
 *   - R2 alone: without R1, leaks are structurally inexpressible but double-spend is not.
 *   - R1 + R2 covers both endpoints: every resource reaches exactly one final state.
 *
 * Agda correspondence: (−⊗A) ⊣ (A*⊗−) (linear ↔ compact-closed adjunction)
 */
export interface ResourceLifecycleRecord {
  /** The resource identifier. */
  readonly id: string;
  /** The terminal lifecycle mode: consumed via R1 or released via R2. */
  readonly mode: ResourceLifecycleMode;
  /** Witness that the resource lifecycle is complete. */
  readonly lifecycleComplete: true;
  /** The dual pair code. */
  readonly pair: "RES";
}

/**
 * Finalize a resource through consumption (R1): consume the handle and record
 * the complete lifecycle. Use this when the resource value will be used.
 */
export function consumeAndRecord<R>(handle: ResourceHandle<R>): {
  value: R;
  record: ResourceLifecycleRecord;
} {
  const h = handle as unknown as { id: string }; // ESCAPE: brand erasure to read id before consume; linear constraint enforced by consume() runtime check, not TypeScript ownership
  const id = h.id;
  const value = consume(handle);
  return {
    value,
    record: { id, mode: "consumed", lifecycleComplete: true, pair: "RES" },
  };
}

/**
 * Finalize a resource through release (R2): release the managed resource and
 * record the complete lifecycle. Use this when the resource must be cleaned up.
 */
export function releaseAndRecord<R>(managed: ManagedResource<R>): ResourceLifecycleRecord {
  const m = managed as unknown as { id: string }; // ESCAPE: brand erasure to read id before release; linearity enforced via runtime tracking in release(), not TypeScript type system
  const id = m.id;
  release(managed);
  return { id, mode: "released", lifecycleComplete: true, pair: "RES" };
}

// ── Metadata constants (one per mode: IMMUNE / CONTAINED / INERT) ─────────

/** Canonical metadata for the DATA.LINEAGE Dual Pair Guarantee (D1+D2, IMMUNE). */
// inline: typed
export const DATA_LINEAGE_META = {
  code: "D1+D2",
  name: "DATA.LINEAGE",
  mode: "immune",
  leftTheorem: "D1",
  rightTheorem: "D2",
  adjunction: "fibration ⊣ opfibration",
  jointStructure: "Bifibration",
  level: 1,
} as const;

/** Canonical metadata for the RES.BALANCE Dual Pair Guarantee (R1+R2, CONTAINED). */
// inline: typed
export const RES_BALANCE_META = {
  code: "R1+R2",
  name: "RES.BALANCE",
  mode: "contained",
  leftTheorem: "R1",
  rightTheorem: "R2",
  adjunction: "(−⊗A) ⊣ (A*⊗−)",
  jointStructure: "Dagger compact category",
  level: 1,
} as const;

/** Canonical metadata for the INV.CONSISTENCY Dual Pair Guarantee (I1+I2, INERT). */
// inline: typed
export const INV_CONSISTENCY_META = {
  code: "I1+I2",
  name: "INV.CONSISTENCY",
  mode: "inert",
  leftTheorem: "I1",
  rightTheorem: "I2",
  adjunction: "F-algebra ⊣ F-coalgebra",
  jointStructure: "Bialgebra / bisimulation",
  level: 1,
} as const;

/** All three DPG metadata entries — exactly one per neutralization mode. */
export const DUAL_PAIR_REGISTRY = [
  DATA_LINEAGE_META,
  INV_CONSISTENCY_META,
  RES_BALANCE_META,
] as const;
